import { IActionFn } from '@/store/types';
import { IModuleState } from './types';
import { ITreeParams } from './types';
import { $http, $asset } from '@/axios';
import store from '@/store';

function navName(payload: ITreeParams) {
  const name = payload.entityType;
  const code = payload.brandCode ? `_${payload.brandCode}` : '';
  const time = (payload.timestamp && payload.timestamp !== 0) ? `_${payload.timestamp}` : '';
  return name + code + time;
}

// 查询导航树
export const queryDigitalNavTree: IActionFn<IModuleState> = ({ commit, state }, payload) => {
  const compName = payload ? `Component_${payload}` : 'Component';
  const furName = payload ? `Furniture_${payload}` : 'Furniture';
  const fetchCompNav = $http.get(`/v1/baseinfo/nav/${compName}/detail`);
  const fetchFurNav = $http.get(`/v1/baseinfo/nav/${furName}/detail`);
  commit('setDigitalNavTree', { ...state.tree, loading: true });

  Promise.all([fetchCompNav, fetchFurNav]).then((results) => {
    const [compTree = null, furTree = null] = results || [];

    // 注意: compTree 的位置不能换
    // 这里表示 compTree 的根节点里没有 id 时, 则 id 值用 -1 填充; 有 id 时, 则使用请求结果里的 id
    const compRoot = { id: -1, entityType: 'Component', ...compTree, name: '零件' };
    const furRoot = { id: -2, entityType: 'Furniture', ...furTree, name: '模块' };
    commit('setDigitalNavTree', { raw: [compRoot, furRoot] });
  }).catch(() => {
    commit('setDigitalNavTree', { data: [] });
  });
};

// 查询图片列表 - 即将废弃
// export const queryDigitalImgList: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
//   try {
//     const { entity, ...rest } = payload;
//     const postMap: Record<string, any> = {
//       Component: '/v1/asset/component/testsearchconf',
//       Furniture: '/v1/asset/block/testsearchconf',
//     };
//     commit('setDigitalImgList', { ...state.images, loading: true });
//     const res: IPageData = await $http.post(postMap[entity], rest);
//     commit('setDigitalImgList', {
//       data: res.list, pageNum: res.pageNum, pageSize: res.pageSize,
//       total: res.total, params: payload,
//     });
//   } catch (e) {
//     commit('setDigitalImgList', { data: [], pageNum: 0, pageSize: 0, total: 0, params: null });
//   }
// };

export const queryDigitalImgList: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDigitalImgList', { ...state.images, loading: true });
    const { list, pageNum, pageSize, total } = await $http.post('/v1/baseinfo/nav/preview', payload);
    commit('setDigitalImgList', { params: payload, data: list, pageNum, pageSize, total });
  } catch (e) {
    commit('setDigitalImgList', { data: [], pageNum: 0, pageSize: 0, total: 0, params: null });
  }
};

// 当前编辑的导航树相关
// ==========================================================================================================
// 发布正在编辑的导航树
export const publishNavTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/nav/publish', { name: navName(payload) });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 覆盖正在编辑的导航树
export const reeditNavTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/nav/reedit', { name: navName(payload) });
    commit('setExpanded', { expand: false }); // 收起历史版本列表
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 删除正在编辑的导航树
export const deleteNavTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/nav/delete', { name: navName(payload) });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 删除已发布导航树
export const deletePublishTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/nav/drop', { name: navName(payload) });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 版本相关
// ==========================================================================================================
// 查询导航树版本列表
export const queryVersionList: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  const name = navName(payload);
  const fetchPublishTree = $asset('oss', `data/navigation_${name}.json`, { ignoreCodes: [404] });
  const fetchVersionList = $http.post('/v1/baseinfo/nav/versionlist', { name });

  Promise.all([fetchPublishTree, fetchVersionList]).then((results) => {
    const [publishTree = null, versionList = []] = results || [];

    // 如果'已发布的导航'存在, 则将导航名称加入到版本列表的第一个位置
    if (publishTree) {
      commit('setPublishTree', { raw: [publishTree] }); // 更新已发布导航树 store
      commit('setVersionList', { raw: [name, ...(versionList as string[])] });
    } else {
      commit('setVersionList', { raw: versionList });
    }
  }).catch(() => {
    commit('setMatNavTree', { data: [] });
  });
};

// 查询对应版本的导航树详情
export const queryVersionTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setVersionTree', { data: [], loading: true });
    const res = await $http.get(`/v1/baseinfo/nav/${navName(payload)}/versiondetail`);
    commit('setVersionTree', { raw: res ? [res] : [] });
  } catch (e) {
    commit('setVersionTree', { data: [] });
  }
};

// 发布导航树版本
export const publishVersionTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/nav/publishversion', { name: navName(payload) });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 删除导航树版本
export const deleteVersionTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    await $http.post('/v1/baseinfo/nav/dropversion', { name: navName(payload) });
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 保存导航树版本
export const saveVersionTree: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setLoading', { type: 'save-version', loading: true });
    await $http.post('/v1/baseinfo/nav/saveversion', { name: navName(payload) });
    store.commit('common/setToast', { visible: true, text: '版本保存成功' });
  } finally {
    commit('setLoading', { loading: false });
  }
};

// 节点相关
// ==========================================================================================================
// 新增导航节点
export  const addNavNode: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setDialog', { ...state.dialog, loading: true });
    await $http.post('/v1/baseinfo/nav/addnode', payload);
  } finally {
    commit('setDialog', { visible: false });
  }
};

// 编辑导航节点
export  const editNavNode: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setLoading', { type: 'save-node', loading: true });
    // await $http.post('/navigation/ex/node/modify', payload);
    await $http.post('/v1/baseinfo/nav/node/modify', payload);
  } finally {
    commit('setLoading', { loading: false });
  }
};

// 删除导航节点
export  const deleteNavNode: IActionFn<IModuleState> = async ({ commit, state }, payload) => {
  try {
    commit('setConfirm', { ...state.confirm, loading: true });
    // await $http.post('/navigation/ex/node/delete', payload);
    await $http.post('/v1/baseinfo/nav/node/delete', payload);
  } finally {
    commit('setConfirm', { visible: false });
  }
};

// 排序导航节点
export const sortNavNode: IActionFn<IModuleState> = async (context, payload) => {
  // await $http.post('/navigation/ex/node/sort', payload);
  await $http.post('/v1/baseinfo/nav/node/sort', payload);
};

// 保存节点配置
export const saveNavConfig: IActionFn<IModuleState> = async ({ commit }, payload) => {
  try {
    commit('setLoading', { type: 'save-setting', loading: true });
    // await $http.post('/navigation/ex/node/config/save', payload);
    await $http.post('/v1/baseinfo/nav/node/saveconfig', payload);
    store.commit('common/setToast', { visible: true, text: '保存成功' });
  } finally {
    commit('setLoading', { loading: false });
  }
};

// 字典相关
// ==========================================================================================================
// 查询所有过滤项(实体分类和标签分类)
export const queryAllFilters: IActionFn<IModuleState> = async ({ commit, state }) => {
  try {
    commit('setDigitalCateTree', { ...state.cate, loading: true });
    commit('setDigitalLabelList', { ...state.label, loading: true });
    // const res: any[] = await $http.get('/baseinfo/filter/all');
    const res: any[] = await $http.get('/v1/baseinfo/filter/listbycommand');
    const category = (res || []).filter((item) => item.isTree);
    const labels = (res || []).filter((item) => !item.isTree);
    commit('setDigitalCateTree', { raw: category });
    commit('setDigitalLabelList', { raw: labels });
  } catch (e) {
    commit('setDigitalCateTree', { data: [] });
    commit('setDigitalLabelList', { data: [] });
  }
};

// 查询实体类型字典
export const queryEntityTypes: IActionFn<IModuleState> = async ({ commit, state }) => {
  try {
    commit('setEntityTypes', { ...state.entityTypes, loading: true });
    const res = await $http.get('/common/chooseoptions/FilterItemEntityType');
    commit('setEntityTypes', { data: res });
  } catch (e) {
    commit('setEntityTypes', { data: [] });
  }
};
